import { useNavigation } from 'expo-router';
import { useCallback, useEffect, useRef } from 'react';

import { confirmDialog } from '@/utils/dialog';

export interface UnsavedChangesGuardOptions {
  /** Whether the screen currently holds changes that would be lost on exit. */
  hasUnsavedChanges: boolean;
  /** Dialog title. Defaults to a generic "Discard changes?". */
  title?: string;
  /** Dialog body. Defaults to a generic unsaved-changes warning. */
  message?: string;
  /** Confirm-button label. Defaults to "Discard". */
  confirmLabel?: string;
}

export interface UnsavedChangesGuard {
  /**
   * Bypass the guard for the next navigator-driven dismissal. Call this
   * immediately before a programmatic navigation that intentionally leaves the
   * screen (for example after a successful submit) so the confirm dialog does
   * not fire on data the user already committed.
   */
  allowNextDismiss: () => void;
}

const DEFAULT_TITLE = 'Discard changes?';
const DEFAULT_MESSAGE = "You have unsaved changes. If you leave now, they'll be lost.";
const DEFAULT_CONFIRM = 'Discard';

/**
 * Confirm before a screen with unsaved changes is dismissed, covering every
 * navigator-driven exit: the header back button, the Android hardware /
 * predictive back gesture, and any programmatic pop.
 *
 * The iOS edge-swipe is handled separately. On a native stack a completed
 * swipe-back removes the screen without ever firing `beforeRemove`, so it would
 * slip past the dialog and silently destroy the user's work. Rather than fight
 * that, the guard disables the gesture while there is unsaved data to lose and
 * restores it the instant the form is clean — the swipe stays alive in the
 * common (nothing-to-lose) case, and the only path out while dirty is one that
 * routes through the confirm dialog.
 */
export function useUnsavedChangesGuard({
  hasUnsavedChanges,
  title = DEFAULT_TITLE,
  message = DEFAULT_MESSAGE,
  confirmLabel = DEFAULT_CONFIRM,
}: UnsavedChangesGuardOptions): UnsavedChangesGuard {
  const navigation = useNavigation();
  const bypassRef = useRef(false);

  useEffect(() => {
    navigation.setOptions({ gestureEnabled: !hasUnsavedChanges });
  }, [navigation, hasUnsavedChanges]);

  useEffect(() => {
    const unsubscribe = navigation.addListener('beforeRemove', (e) => {
      if (!hasUnsavedChanges || bypassRef.current) return;

      e.preventDefault();
      confirmDialog(
        title,
        message,
        () => {
          // Let the re-dispatched action through the guard so the confirmed
          // exit doesn't re-trigger the dialog in an endless loop.
          bypassRef.current = true;
          navigation.dispatch(e.data.action);
        },
        undefined,
        confirmLabel,
      );
    });

    return unsubscribe;
  }, [navigation, hasUnsavedChanges, title, message, confirmLabel]);

  const allowNextDismiss = useCallback(() => {
    bypassRef.current = true;
  }, []);

  return { allowNextDismiss };
}
